import React from "react";
import {
  IconButton,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { formatSignedUsdCompact, formatAumMillions } from "../managerOverviewDisplayUtils";
import { fundLabel, smaLabel, fundSmaTypeCellLabel } from "./managerDetailDialogLabels";

const COLGROUP = (
  <colgroup>
    <col style={{ width: 48 }} />
    <col style={{ width: "24%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
  </colgroup>
);

function PnLCells({ cellSx, m, changeTone }) {
  return (
    <>
      <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: changeTone(m.week) }}>
        {formatSignedUsdCompact(m.week)}
      </TableCell>
      <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: changeTone(m.month) }}>
        {formatSignedUsdCompact(m.month)}
      </TableCell>
      <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: changeTone(m.quarter) }}>
        {formatSignedUsdCompact(m.quarter)}
      </TableCell>
      <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: changeTone(m.year) }}>
        {formatSignedUsdCompact(m.year)}
      </TableCell>
      <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: changeTone(m.days365) }}>
        {formatSignedUsdCompact(m.days365)}
      </TableCell>
    </>
  );
}

/**
 * @param {{
 *   kind: "fund" | "sma";
 *   sortedEntries: { key: string; id: unknown; data: unknown }[];
 *   metricsByKey: Map<string, { aum: number; week: number; month: number; quarter: number; year: number; days365: number }>;
 *   strategiesByKey: Map<string, { key: string; name: string; aum: number; week: number; month: number; quarter: number; year: number; days365: number }[]>;
 *   expandedSet: Set<string>;
 *   onToggleExpand: (kind: "fund" | "sma", key: string) => void;
 *   funds: unknown[];
 *   cellSx: object;
 *   hoverChildBg: string;
 *   changeTone: (v: unknown) => string;
 *   tableHead: React.ReactNode;
 *   tableContainerSx?: object;
 * }} props
 */
export function FundSmaExpandableTable({
  kind,
  sortedEntries,
  metricsByKey,
  strategiesByKey,
  expandedSet,
  onToggleExpand,
  funds,
  cellSx,
  hoverChildBg,
  changeTone,
  tableHead,
  tableContainerSx = {},
}) {
  const defaultM = { aum: 0, week: 0, month: 0, quarter: 0, year: 0, days365: 0 };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 0, width: "100%", overflowX: "auto", overflowY: "visible", ...tableContainerSx }}
    >
      <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
        {COLGROUP}
        <TableHead>{tableHead}</TableHead>
        <TableBody>
          {sortedEntries.map(({ key, id, data }) => {
            const m = metricsByKey.get(key) ?? defaultM;
            const exp = expandedSet.has(`${kind}:${key}`);
            const stratRows = strategiesByKey.get(key) ?? [];
            const primaryLabel = kind === "fund" ? fundLabel(id, data, funds) : smaLabel(id, data);
            return (
              <React.Fragment key={key}>
                <TableRow hover>
                  <TableCell sx={{ ...cellSx, py: 0.5, width: 48 }}>
                    <IconButton
                      size="small"
                      aria-expanded={exp}
                      aria-label={exp ? "Collapse strategies" : "Expand strategies"}
                      onClick={() => onToggleExpand(kind, key)}
                    >
                      {exp ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 0,
                    }}
                  >
                    <Typography fontWeight={600} noWrap component="span">
                      {primaryLabel}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>{fundSmaTypeCellLabel(kind, data)}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}>
                    {formatAumMillions(m.aum)}
                  </TableCell>
                  <PnLCells cellSx={cellSx} m={m} changeTone={changeTone} />
                </TableRow>
                {exp &&
                  stratRows.map((c) => (
                    <TableRow key={`${key}-s-${c.key}`} sx={{ backgroundColor: hoverChildBg }}>
                      <TableCell sx={cellSx} />
                      <TableCell
                        sx={{
                          ...cellSx,
                          pl: 3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 0,
                        }}
                      >
                        <Typography component="span" noWrap>
                          {c.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>Strategy</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}>
                        {formatAumMillions(c.aum)}
                      </TableCell>
                      <PnLCells cellSx={cellSx} m={c} changeTone={changeTone} />
                    </TableRow>
                  ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
